import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-canvas to-canvas flex justify-center">
      <div className="w-full max-w-md px-6 py-8">
        <button
          onClick={() => navigate('/')}
          className="font-body text-lime text-sm mb-6 inline-flex items-center gap-1"
        >
          &larr; Back to Suppertide
        </button>

        <h1 className="font-display text-3xl font-semibold text-ink mb-2">
          Privacy Policy
        </h1>
        <p className="font-body text-ink-soft text-sm mb-6">
          Effective February 15, 2026 &middot; Updated June 16, 2026
        </p>

        <div className="space-y-4">
          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              Who We Are
            </h2>
            <p className="font-body text-ink-soft text-sm leading-relaxed">
              Suppertide is a family meal planning application operated by
              Sophinea. This policy describes how we collect, use, and protect
              your information when you use our service.
            </p>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              Data We Collect
            </h2>
            <ul className="font-body text-ink-soft text-sm leading-relaxed list-disc pl-4 space-y-1">
              <li>Email address (for authentication)</li>
              <li>Household information</li>
              <li>Family member names and types (adult/kid)</li>
              <li>Meal preferences (swipes)</li>
              <li>Meal feedback (ratings and comments)</li>
              <li>Recipe content you create</li>
              <li>Recipe images you upload</li>
            </ul>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              How We Store Your Data
            </h2>
            <p className="font-body text-ink-soft text-sm leading-relaxed">
              Your data is stored securely using Supabase (PostgreSQL) hosted
              infrastructure. Static assets are served via Cloudflare Pages.
              Recipe images are stored in Supabase Storage.
            </p>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              Third-Party Services
            </h2>
            <p className="font-body text-ink-soft text-sm leading-relaxed mb-2">
              We use the following third-party services to operate Suppertide:
            </p>
            <ul className="font-body text-ink-soft text-sm leading-relaxed list-disc pl-4 space-y-1">
              <li>
                <span className="font-semibold">Supabase</span> &mdash;
                authentication and database
              </li>
              <li>
                <span className="font-semibold">Cloudflare</span> &mdash;
                hosting and CDN
              </li>
              <li>
                <span className="font-semibold">Plausible</span> &mdash;
                privacy-friendly, cookieless usage analytics (no personal data,
                no cross-site tracking)
              </li>
              <li>
                <span className="font-semibold">Sentry</span> &mdash;
                crash and error diagnostics to keep the app stable
              </li>
            </ul>
            <p className="font-body text-ink-soft text-sm leading-relaxed mt-2">
              We do not sell your data or share it with third-party advertisers.
            </p>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              Children&rsquo;s Privacy (COPPA)
            </h2>
            <p className="font-body text-ink-soft text-sm leading-relaxed">
              Suppertide is designed for use by families, including children.
              We do not collect data directly from children under 13. A parent or
              guardian must create and manage the account. Children&rsquo;s data
              is limited to their name, type (kid), swipe preferences, and meal
              feedback. All child profiles are created and managed by the
              parent/guardian account holder.
            </p>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              Cookies &amp; Local Storage
            </h2>
            <p className="font-body text-ink-soft text-sm leading-relaxed">
              We store a Supabase authentication token in your browser&rsquo;s
              localStorage to keep you signed in. Our usage analytics (Plausible)
              are cookieless and do not track you across sites or apps, and we do
              not use third-party advertising cookies.
            </p>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              Data Retention &amp; Deletion
            </h2>
            <p className="font-body text-ink-soft text-sm leading-relaxed">
              Your data is retained for as long as your account is active. If
              you delete your account, all associated data is permanently removed
              via cascading deletion, including household data, family member
              profiles, recipes, preferences, and feedback. See{' '}
              <button
                onClick={() => navigate('/delete-account')}
                className="text-lime underline"
              >
                how to delete your account &amp; data
              </button>
              .
            </p>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              Data Export
            </h2>
            <p className="font-body text-ink-soft text-sm leading-relaxed">
              You may request an export of all your data by contacting us. We are
              working on a self-service data export feature.
            </p>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              Changes to This Policy
            </h2>
            <p className="font-body text-ink-soft text-sm leading-relaxed">
              We may update this privacy policy from time to time. We will notify
              you of changes through the app. Continued use of Suppertide after
              changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section className="bg-panel rounded-2xl p-5 border border-hairline">
            <h2 className="font-display text-lg font-bold text-ink mb-2">
              Contact Us
            </h2>
            <p className="font-body text-ink-soft text-sm leading-relaxed">
              If you have questions about this privacy policy, contact us at{' '}
              <a
                href="mailto:hello@suppertide.com"
                className="text-lime underline"
              >
                hello@suppertide.com
              </a>
              .
            </p>
          </section>
        </div>

        <p className="font-body text-ink-soft text-xs text-center mt-6">
          &copy; 2026 Sophinea. All rights reserved.
        </p>
      </div>
    </div>
  );
}
